import { Chip, Grid, Stack, TextField, Typography } from '@mui/material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import SmartSelect from '../generic/smartSelect/SmartSelect'
import { isJobPriorityValid, toJobPriorityFieldValue } from './utils/jobPriority'

/**
 * @typedef {import("../generic/utilities/objectStructures/planner/jobSettings").JobSettings} JobSettings JobSettings object
 */

/**
 * "Job Settings" tab of the planner event dialog (story 96, CA6/CA9/CA11).
 *
 * The three fields the planner can fix on a JOB -- they live in the process, so they survive
 * replanning, publishing and resetting the plan (P2), unlike anything the planner itself decides:
 *
 *  - queue priority (`jobPriority`): orders the MOS lists. Labelled just "Priority" with the visible
 *    "1 = first" hint, because it is THE priority for the operator (D24). The Part's own 1..10
 *    priority keeps its own, qualified label in the sibling tab -- the two must never read the same
 *    (RN38/R4).
 *  - remark: the same remark the workflow editor edits, last-write-wins (RN33/CA13).
 *  - preferred machine: the ONLY place a machine is fixed now that the per-task machine pin is gone
 *    (RN37/P6). Job-grained: it applies to every part and task of the job (RN18/D3).
 *
 * The machine selector is disabled with an explanation for a job in a synchronised planning group
 * (#93): there the block's device governs the machine and the change would be ignored in silence, so
 * it is not offered at all (RN25/P3/A3). Priority and remark stay editable in that case (CA9).
 *
 * Story 146 / S68 (RN31.1/D31): the very same treatment for an EXTERNAL job, which is planned on an
 * external asset and therefore has no preferred one. The selector is disabled AND shown empty --
 * even if the read still returns the machine fixed before the node was marked, it is not offered as
 * a choice (the same the workflow editor does in S63). Belt and braces: RN27/RN28 clear the datum on
 * write, this stops the planner from believing they chose something the engine ignores.
 *
 * @param {object} props
 * @param {JobSettings} props.jobSettings Values currently stored for the job, plus the compatible
 * machine offer and whether the job belongs to a synchronised planning group
 * @param {JobSettings} props.newJobSettings Values being edited
 * @param {function(JobSettings)} props.onChangeNewJobSettings Handles a change on the edited values
 * @param {boolean} props.showErrors Whether validation errors must be surfaced (set on accept)
 * @returns
 */
const JobSettingsDialogContent = ({ jobSettings, newJobSettings, onChangeNewJobSettings, showErrors }) => {
  const { t_ } = useSmartTranslation()

  if (!jobSettings || !newJobSettings) {
    return null
  }

  // V6/RN25: resolved by the server and merely reflected here. The selector is prevention, not the
  // guarantee -- Sun rejects the change too (P5).
  const governedByPlanningGroup = jobSettings.planningGroupMember === true
  // S68/RN64: the job's own mark, read from `GET planner/jobSettings/{id}` (03-dominio.md §5.8). It
  // is `extern` because it comes from the node, not from a device -- `external` is the machine's
  // mark and is NOT read here. Absent or false means internal (RN64.2).
  const externalJob = jobSettings.extern === true
  const priorityValue = toJobPriorityFieldValue(newJobSettings.jobPriority)
  const priorityError = showErrors && !isJobPriorityValid(priorityValue)
  // An external job has no preferred machine to show, whatever was stored before it was marked.
  const preferredMachine = externalJob ? '' : (newJobSettings.preferenceDevice ?? '')
  const machineFixed = Boolean(preferredMachine)
  const compatibleDevices = jobSettings.compatibleDevices ?? []
  // S20/RN20: the job's preferred machine may NOT be among the compatible ones -- a machine fixed
  // earlier can stop supporting the job's technology later. Sun keeps returning it as the current
  // value, and it has to stay visible: emptying the selector would hide the very reason the whole
  // fabrication order fails to plan. It is deliberately NOT added to the offer (only compatible
  // machines can be fixed, RN19), so it shows as the selected value with an explanation beside it.
  const fixedDeviceNoLongerCompatible = machineFixed && !compatibleDevices.includes(preferredMachine)

  return (
    // Two columns on purpose. The queue priority is two digits at most, so giving it the dialog's full
    // width read as unfinished; and the machine's state belongs next to the machine, not stacked under
    // three other captions. The remark, which is free text, keeps the full width below.
    <Grid container spacing={2} sx={{ marginTop: '0.25em' }}>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label={t_('Priority')}
          value={priorityValue}
          onChange={(event) => onChangeNewJobSettings({ ...newJobSettings, jobPriority: event.target.value })}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          error={priorityError}
          // Always visible, not only on error: the scale is ascending and the Part's is not, so the
          // hint is what stops the planner from reading "10" as "most urgent" (RN6/D20/D24). Keeping
          // the slot always filled also stops the row from jumping when the error appears.
          helperText={priorityError ? t_('Value must be an integer greater than or equal to 1') : t_('1 = first')}
        />
      </Grid>
      <Grid item xs={12} sm={8}>
        <Stack gap={0.75}>
          <SmartSelect
            autoSelectFirst={false}
            label={t_('Preferred machine')}
            selectableOptions={compatibleDevices}
            value={preferredMachine}
            disabled={governedByPlanningGroup || externalJob}
            disableClearable={false}
            onChange={(newValue) => onChangeNewJobSettings({ ...newJobSettings, preferenceDevice: newValue })}
          />
          {/* RN26/CA11: "machine fixed" is job-grained and derived from ONE thing -- whether the job
              has a preferred machine. It is never read from the plan or from a task, so it reads the
              same whichever part the dialog was opened from. A read-only chip, NOT a toggle: the way
              to unfix a machine is to clear the selector (CU4). It is a chip rather than one more
              caption so the state does not compete with the two warnings below it. */}
          <Chip
            size='small'
            variant='outlined'
            color={machineFixed ? 'primary' : 'default'}
            label={machineFixed ? t_('Machine fixed') : t_('Machine not fixed')}
            sx={{ alignSelf: 'flex-start' }}
          />
          {externalJob && (
            <Typography variant='caption' color='text.secondary'>
              {t_('An external job is planned on an external asset')}
            </Typography>
          )}
          {governedByPlanningGroup && (
            <Typography variant='caption' color='text.secondary'>
              {t_('The planning group governs the machine of this job')}
            </Typography>
          )}
          {fixedDeviceNoLongerCompatible && (
            <Typography variant='caption' color='warning.main'>
              {t_('This machine no longer supports the technology of this job')}
            </Typography>
          )}
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label={t_('Remark')}
          value={newJobSettings.remark ?? ''}
          onChange={(event) => onChangeNewJobSettings({ ...newJobSettings, remark: event.target.value })}
          multiline
          minRows={2}
        />
      </Grid>
    </Grid>
  )
}

export default JobSettingsDialogContent
